import React, { useEffect, useState } from "react";

import { CircularProgress, Grid, Tooltip } from "@mui/material";
import { DoneAll } from "@mui/icons-material";
import ShipeeziGrid, {
  Column,
  GeneralActions,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { FiEye } from "react-icons/fi";
import { HiFlag } from "react-icons/hi";
import { useHistory } from "react-router-dom";
import { Badge } from "reactstrap";

import { PRIVATE_ROUTES } from "../../constants";
import { useAppState } from "../../context/StateContext";
import { useNotificationHook } from "../../hooks/useNotificationActions";
import NotificationViewComponent from "../../layout/Sidebar/NotificationBlock/NotificationsListDropdown/NotificationViewComponent";
import {
  Notification,
  NotificationsRow,
  NotificationUpdateAction,
} from "../../models/notification";
import { User } from "../../models/user";
import { NotificationAction, NotificationResourceName, NotificationsTypes } from "../../util/enums";
import { saveRowsSelectedFromTable } from "../../util/selectRowsFromTable";
import Button, { Color } from "../Form/Button";
import BadgeNotificationType from "../Generic/BadgeNotificationType";
import TitlePage from "../TitlePage";
import useStyles from "./styles";
import { AppDynamicContent } from "Components/AppDynamicContent";

const NotificationsScreen = () => {
  const {
    notifications,
    page,
    pageSize,
    rows,
    setRows,
    total,
    getNotificationCount,
    getNotificationsList,
    setPageSize,
    handleCurrentPage,
    handleSearch,
    handleFilter,
    handleSorting,
    notificationAction,
    setNotificationAction,
    refetchUnreadNotificationsCount,
    useUpdateNotification,
  } = useNotificationHook();

  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const [quoteId, setQuoteId] = useState<number | undefined>(undefined);
  const [orderId, setOrderId] = useState<number | undefined>(undefined);

  const [resourceName, setResourceName] = useState<string>("");
  const [openViewModal, setOpenViewModal] = useState<boolean>(false);

  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const params = new URLSearchParams(window.location.search);
  const urlId = params.get("id");
  const urlType = params.get("type");
  const history = useHistory();

  useEffect(() => {
    if (urlId && urlType) {
      setQuoteId(Number(urlId));
      setOrderId(Number(urlId));
      setOpenViewModal(true);
      setResourceName(urlType);
    } else {
      setQuoteId(undefined);
      setOrderId(undefined);
      setResourceName("");
    }
  }, [params]);

  const { isFetching: isLoading } = getNotificationsList();

  getNotificationCount();

  refetchUnreadNotificationsCount();

  const { mutate: updateNotification, isLoading: isLoadingNotificationUpdate } =
    useUpdateNotification();

  const columns: Column[] = [
    {
      name: "type",
      title: translate("type"),
      formatComponent: (type?: string) => <BadgeNotificationType value={type} />,
      hideSearch: true,
    },
    {
      name: "description",
      title: translate("description"),
      formatText: (value?: string) => value && <AppDynamicContent htmlString={value} />,
    },
    {
      name: "fromUser",
      title: translate("user"),
      hideSearch: true,
      formatText: (fromUser?: User) =>
        fromUser?.name && `${fromUser.name} ${fromUser.lastName ?? ""}`,
    },
    {
      name: "updatedAt",
      title: translate("dateUpdated"),
      hideSearch: true,
      formatComponent: (updatedAt?: Date) =>
        updatedAt && (
          <Grid item className={classes.dateUpdated}>
            <div>
              <span>{format(new Date(updatedAt), "dd/MM/yyyy")}</span>
            </div>
            <div>
              <span>{format(new Date(updatedAt), "hh:mm a")}</span>
            </div>
          </Grid>
        ),
    },
  ];

  const changeMarkAsRead = (notificationId: number) => {
    const findNotification = notifications.find(
      (notification) => Number(notification.id) === Number(notificationId)
    );

    if (findNotification) {
      setNotificationAction({
        ids: [notificationId],
        action: NotificationAction.READ,
      });

      updateNotification({
        id: Number(notificationId),
        notification: {
          ...findNotification,
          read: !(findNotification.read && findNotification.read === true),
        },
        action: NotificationAction.READ,
      });
    }
  };

  const changeMarkAsImportant = (notificationId: number) => {
    const findNotification = notifications.find(
      (notification) => Number(notification.id) === Number(notificationId)
    );

    if (findNotification) {
      setNotificationAction({
        ids: [notificationId],
        action: NotificationAction.IMPORTANT,
      });

      updateNotification({
        id: Number(notificationId),
        notification: {
          ...findNotification,
          important: !(findNotification.important && findNotification.important === true),
        },
        action: NotificationAction.IMPORTANT,
      });
    }
  };

  const handleSetUrl = (resourceName?: string, resourceNumberId?: number) => {
    if (resourceName && resourceNumberId) {
      return history.push(
        `${PRIVATE_ROUTES.NOTIFICATIONS}?type=${resourceName}&id=${resourceNumberId}`
      );
    }

    return history.push(`${PRIVATE_ROUTES.NOTIFICATIONS}`);
  };

  const handleOpenSideBarViewQuote = (resourceNumberId?: number) => {
    setQuoteId(resourceNumberId || undefined);
  };

  const handleOpenSideBarViewOrder = (resourceNumberId?: number) => {
    setOrderId(resourceNumberId || undefined);
  };

  const handleViewNotificationResource = (id: number, row: Notification) => {
    if (!row.resourceName || !row.resourceNumberId) {
      return <FiEye color="#98A2B3" size={18} />;
    }

    return (
      <Tooltip title={translate("view")} placement="top">
        <div>
          <FiEye
            onClick={() => {
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              row.resourceName && row.resourceName === NotificationResourceName.QUOTE
                ? handleOpenSideBarViewQuote(row.resourceNumberId)
                : handleOpenSideBarViewOrder(row.resourceNumberId);
              setResourceName(row.resourceName ? row.resourceName : "");
              handleSetUrl(row.resourceName, row.resourceNumberId);
              setOpenViewModal(true);
              setTimeout(() => setOpenViewModal(false), 500);
            }}
            color="#475467"
            className={classes.button}
            size={18}
            data-cy="viewTriggeredIcon"
          />
        </div>
      </Tooltip>
    );
  };

  const handleMarkAsReadButton = (id: number, row: Notification) => {
    if (
      notificationAction?.ids?.some((notificationId) => notificationId === id) &&
      notificationAction?.action === NotificationAction.READ &&
      isLoadingNotificationUpdate
    ) {
      return <CircularProgress className={classes.loadingIcon} size={18} />;
    }

    return (
      <Tooltip
        title={row.read ? translate("markAsUnread") : translate("markAsRead")}
        placement="top"
      >
        <div>
          <DoneAll
            className={classes.readIcon}
            onClick={() => changeMarkAsRead(id)}
            htmlColor={row.read ? "#32D583" : "#667085"}
            data-cy="markAsReadIcon"
          />
        </div>
      </Tooltip>
    );
  };

  const handleImportantButton = (id: number, row: Notification) => {
    if (
      notificationAction?.ids?.some((notificationId) => notificationId === id) &&
      notificationAction?.action === NotificationAction.IMPORTANT &&
      isLoadingNotificationUpdate
    ) {
      return <CircularProgress className={classes.loadingIcon} size={18} />;
    }

    return (
      <Tooltip
        title={row.important ? translate("unmarkAsImportant") : translate("markAsImportant")}
        placement="top"
      >
        <div>
          <HiFlag
            className={classes.importantIcon}
            onClick={() => changeMarkAsImportant(id)}
            color={row.important ? "#32D583" : "#667085"}
            data-cy="markAsImportantIcon"
          />
        </div>
      </Tooltip>
    );
  };

  const handleSaveNotificationSelected = (rowsArr: number[]) => {
    setClearSelection(false);
    const rowsSelected: NotificationsRow[] = saveRowsSelectedFromTable(
      rowsArr,
      rows,
      notifications
    );

    setRows([...rowsSelected]);
  };

  const handleMarkSelectedNotificationsAsReadOrUnread = () => {
    if (rows && rows.length > 0) {
      setNotificationAction({
        ids: rows.map((row) => row.id).filter(Boolean) as NotificationUpdateAction["ids"],
        action: NotificationAction.READ,
      });
      const unreadNotification = rows.find((row) => row.read === false);

      rows.forEach((row) => {
        if (row.id) {
          updateNotification({
            id: Number(row.id),
            notification: {
              read: !!unreadNotification,
            } as Notification,
            action: unreadNotification ? NotificationAction.ALL_READ : NotificationAction.READ,
          });
        }
      });
      setRows([]);
      setClearSelection(true);
    }
  };

  const renderButtonToMarkAsReadOrUnread = () => {
    if (rows.length > 0) {
      const unreadNotification = rows.find((row) => row.read === false);

      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.markAsUnreadButton}
          onClick={handleMarkSelectedNotificationsAsReadOrUnread}
          customColor={Color.SECONDARY}
          data-cy="markAsUnread"
        >
          {unreadNotification ? translate("markAsRead") : translate("markAsUnread")}
        </Button>
      );
    }
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: "view",
      key: "view",
      buttonAndAction: handleViewNotificationResource,
    },
    {
      title: "read",
      key: "read",
      buttonAndAction: handleMarkAsReadButton,
    },
    {
      title: "important",
      key: "important",
      buttonAndAction: handleImportantButton,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleMarkAsUnread"],
    },
    handleSearch: {
      onSearch: (filters) => handleSearch(filters),
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("type"),
          column: "type",
          options: [{ value: NotificationsTypes.WEB, label: translate("web") }],
        },
        {
          name: translate("importance"),
          column: "important",
          options: [
            { value: "1", label: translate("important") },
            { value: "0", label: translate("notImportant") },
          ],
        },
      ],
      onChange: handleFilter,
    },
    handleMarkAsUnread: renderButtonToMarkAsReadOrUnread(),
  };

  return (
    <div data-cy="notifications-centre-page">
      <TitlePage
        title={translate("notifications")}
        right={
          currentUser &&
          currentUser?.company?.profile &&
          currentUser?.company?.profile?.name && (
            <div>
              <Badge className={classes.headerBadge}>
                {currentUser.company.status} {translate("company")}
              </Badge>
              <span className={classes.headerVersion} data-cy="company-profile-name">
                {translate("version")} |<strong> {currentUser.company.profile?.name}</strong>
              </span>
            </div>
          )
        }
      />
      <div className={classes.gridWrapper}>
        <ShipeeziGrid
          data={notifications}
          columns={columns}
          totalItems={total}
          loadingData={isLoading}
          emptyData={
            isLoading
              ? `${translate("loadingNotifications")}...`
              : translate("noInformationToBeShown")
          }
          pageSize={pageSize}
          pageSizeList={[20, 40, 60, 100]}
          onChangePageSize={setPageSize}
          currentPage={page}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          generalActions={generalActions}
          handleSelect={(rowsArr: number[]) => handleSaveNotificationSelected(rowsArr)}
          clearSelection={clearSelection}
          onSorting={handleSorting}
          rowActions={{
            orderBy: ["view", "read", "important"],
          }}
          rowActionsExtra={rowActionsExtra}
          positionActionsColumn="right"
        />
        <NotificationViewComponent
          resourceName={resourceName}
          quoteId={quoteId}
          orderId={orderId}
          openModal={openViewModal}
          setOpenModal={setOpenViewModal}
          onCloseViewComponent={handleSetUrl}
        />
      </div>
    </div>
  );
};

export default NotificationsScreen;

export { default as FCMInitializer } from "./FCMInitializer";
